import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "./Button";
import { StarRating } from "./StarRating";
import { Confetti } from "./Confetti";
import type { DisciplineSlug } from "@jumpbean/shared";

export interface LevelCompleteModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Level number completed */
  levelNumber: number;
  /** Total stars earned in the level */
  starsEarned: number;
  /** Maximum possible stars */
  maxStars: number;
  /** Discipline for styling */
  discipline?: DisciplineSlug;
  /** Continue to next level callback */
  onContinue?: () => void;
  /** Go back to level selection */
  onBack: () => void;
}

/**
 * Celebratory modal displayed when a child completes all challenges in a level.
 * Shows total stars earned and options to continue or go back.
 */
export function LevelCompleteModal({
  isOpen,
  levelNumber,
  starsEarned,
  maxStars,
  discipline = "math",
  onContinue,
  onBack,
}: LevelCompleteModalProps) {
  const [showContent, setShowContent] = useState(false);
  const [showStars, setShowStars] = useState(false);
  const [showButtons, setShowButtons] = useState(false);

  // Calculate star rating (0-3 scale)
  const starRating = Math.min(3, Math.round((starsEarned / maxStars) * 3)) as 0 | 1 | 2 | 3;

  // Determine performance message
  const getPerformanceMessage = () => {
    const percentage = (starsEarned / maxStars) * 100;
    if (percentage >= 90) return { title: "Perfect!", subtitle: "Outstanding work!" };
    if (percentage >= 70) return { title: "Great Job!", subtitle: "You did wonderfully!" };
    if (percentage >= 50) return { title: "Well Done!", subtitle: "Keep practicing!" };
    return { title: "Level Complete!", subtitle: "You can do even better next time!" };
  };

  const message = getPerformanceMessage();

  // Discipline-specific colors
  const disciplineColors: Record<DisciplineSlug, string> = {
    math: "from-luna-light to-magic-400",
    spelling: "from-petal-light to-meadow-400",
    patterns: "from-sunshine-200 to-fairy-400",
    typing: "from-whiskers-light to-sky-500",
    programming: "from-pixel-light to-pixel-darker",
  };

  // Animate in stages
  useEffect(() => {
    if (isOpen) {
      setShowContent(false);
      setShowStars(false);
      setShowButtons(false);

      const contentTimer = setTimeout(() => setShowContent(true), 200);
      const starsTimer = setTimeout(() => setShowStars(true), 600);
      const buttonsTimer = setTimeout(() => setShowButtons(true), 1000);

      return () => {
        clearTimeout(contentTimer);
        clearTimeout(starsTimer);
        clearTimeout(buttonsTimer);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="
          absolute inset-0
          bg-gradient-to-b from-magic-900/50 to-night/70
          backdrop-blur-sm
          animate-[fadeIn_300ms_ease-out]
        "
      />

      {/* Confetti celebration */}
      <Confetti isActive={isOpen} count={80} duration={3000} />

      {/* Modal container */}
      <div
        className="
          relative
          w-full max-w-md
          bg-cloud
          rounded-3xl
          shadow-[0_20px_60px_rgba(0,0,0,0.3)]
          border-4 border-meadow-300
          overflow-hidden
          animate-[levelCompleteSlideIn_500ms_cubic-bezier(0.34,1.56,0.64,1)]
        "
      >
        {/* Decorative header gradient */}
        <div
          className={`
            h-24
            bg-gradient-to-r ${disciplineColors[discipline]}
            flex items-center justify-center
          `}
        >
          <div
            className={`
              font-display font-bold text-display-md text-white
              drop-shadow-lg
              transition-all duration-500 transform
              ${showContent ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0"}
            `}
          >
            Level {levelNumber}
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-8 text-center">
          {/* Title and subtitle */}
          <div
            className={`
              mb-6
              transition-all duration-500 transform
              ${showContent ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}
            `}
          >
            <h2 className="font-display font-bold text-display-md text-meadow-600 mb-2">
              {message.title}
            </h2>
            <p className="font-body text-body-lg text-neutral-600">
              {message.subtitle}
            </p>
          </div>

          {/* Star rating */}
          <div
            className={`
              flex justify-center mb-6
              transition-all duration-700 transform
              ${showStars ? "scale-100 opacity-100" : "scale-50 opacity-0"}
            `}
          >
            <StarRating
              stars={starRating}
              size="xl"
              animated={showStars}
              isNewResult
            />
          </div>

          {/* Stars count */}
          <div
            className={`
              mb-8
              transition-all duration-500 transform delay-200
              ${showStars ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}
            `}
          >
            <p className="font-display text-body-xl text-neutral-700">
              <span className="font-bold text-sunshine-600">{starsEarned}</span>
              <span className="text-neutral-500"> / {maxStars} stars earned</span>
            </p>
          </div>

          {/* Action buttons */}
          <div
            className={`
              flex flex-col gap-3
              transition-all duration-500 transform
              ${showButtons ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}
            `}
          >
            {onContinue && (
              <Button
                variant="primary"
                size="lg"
                onClick={onContinue}
                className="w-full"
              >
                Next Level
              </Button>
            )}
            <Button
              variant="secondary"
              size="lg"
              onClick={onBack}
              className="w-full"
            >
              Back to Levels
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

// Add level complete animation if not present
if (typeof document !== "undefined") {
  const styleId = "jumpbean-level-complete-animations";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes levelCompleteSlideIn {
        0% {
          opacity: 0;
          transform: scale(0.9) translateY(30px);
        }
        100% {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }
    `;
    document.head.appendChild(style);
  }
}

export default LevelCompleteModal;
